/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import EditIcon from '@material-ui/icons/Edit';
import {
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  TableBody,
  IconButton,
  TablePagination,
} from '@material-ui/core';
import {
  getEnabledText,
  getRPPStorageKey,
  getPageStorageKey,
} from 'helpers/programs';
import { useHistory, useLocation } from 'react-router-dom';
import { Program, StorageKey, NavigationBack } from 'models';
import { HeadCell } from 'models/tables';
import { EDIT_PROGRAM } from 'constants/routes';
import TableHead from 'components/tables/TableHead';
import TableLoading from 'components/loading/TableLoading';
import usePrograms from 'hooks/usePrograms';
import useLocalStorage from 'hooks/useLocalStorage';
import useStyles from './style';

interface Props {
  isRequired: boolean;
}

const headCells: HeadCell[] = [
  { id: 'name', label: 'Name' },
  { id: 'description', label: 'Description' },
  { id: 'options', label: 'Options' },
  { id: 'enabled', label: 'Enabled' },
  { id: 'edit', label: 'Edit' },
];

const ProgramTable: React.FC<Props> = (props) => {
  const { isRequired } = props;
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { get, set } = useLocalStorage();
  const { getAll, programs, loading } = usePrograms();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [rppStorageKey, setRppStorageKey] = useState<StorageKey>();
  const [pageStorageKey, setPageStorageKey] = useState<StorageKey>();

  // Local Storage.
  const getRPPFromLocalStorage = async (): Promise<void> => {
    const key = getRPPStorageKey(isRequired);
    const rows = await get<number>(key);
    if (rows) setRowsPerPage(rows);
    setRppStorageKey(key);
  };

  const getPageFromLocalStorage = async (): Promise<void> => {
    const key = getPageStorageKey(isRequired);
    const params = location.state as NavigationBack;
    let currentPage = 0;
    if (history.action === 'PUSH' && params?.goBack) {
      const newPageNumber = await get<number>(key);
      if (newPageNumber) {
        currentPage = newPageNumber;
      }
    }
    setPage(currentPage);
    setPageStorageKey(key);
  };

  const setToLocalStorage = async (
    key: StorageKey,
    rows: number,
  ): Promise<void> => {
    await set(key, rows);
  };

  // Handlers.
  const handleChangePage = (_: unknown, newPage: number): void => {
    setPage(newPage);
    setToLocalStorage(pageStorageKey as StorageKey, newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const rowsNumber = parseInt(event.target.value, 10);
    setRowsPerPage(rowsNumber);
    setPage(0);
    setToLocalStorage(rppStorageKey as StorageKey, rowsNumber);
    setToLocalStorage(pageStorageKey as StorageKey, 0);
  };

  const handleEdit = (program: Program): void => {
    history.push(EDIT_PROGRAM, { program });
  };

  // Effects.
  useEffect(() => {
    getRPPFromLocalStorage();
    getPageFromLocalStorage();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    getAll(isRequired);
  }, []);

  return (
    <div className={classes.container}>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead cells={headCells} />
            <TableBody>
              {loading && <TableLoading colSpan={headCells.length} />}
              {!loading &&
                programs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: Program) => (
                    <TableRow key={row.id}>
                      <TableCell align='left'>{row.name}</TableCell>
                      <TableCell align='left'>{row.description}</TableCell>
                      <TableCell align='left'>{row.options.length}</TableCell>
                      <TableCell align='left'>
                        {getEnabledText(row.enabled)}
                      </TableCell>
                      <TableCell align='left'>
                        <IconButton
                          aria-label='edit'
                          onClick={(): void => handleEdit(row)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && (
          <TablePagination
            page={page}
            component='div'
            count={programs.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </div>
  );
};

export default ProgramTable;
